import { Id, WorkLoad, WorkLoadsCalendar } from "@planzone/dto";
import { Request, Selector, State, View } from "./types";

function findEntry(
  state: State,
  view: View,
  resourceUuid: Id,
): Request | undefined {
  return state.find(
    (req) => req.view === view && req.resourceUuid === resourceUuid,
  );
}

function dedupByKey<T>(items: T[], keyOf: (item: T) => string): T[] {
  const seen = new Map<string, T>();
  for (const item of items) seen.set(keyOf(item), item);
  return Array.from(seen.values());
}

function mergeChunks(chunks: WorkLoad[]): WorkLoad {
  const first = chunks[0];
  return {
    ...first,
    totalOfEstimatedHours: chunks.reduce(
      (sum, c) => sum + c.totalOfEstimatedHours,
      0,
    ),
    totalOfAllocatedHours: chunks.reduce(
      (sum, c) => sum + c.totalOfAllocatedHours,
      0,
    ),
    totalOfSpentTime: chunks.reduce((sum, c) => sum + c.totalOfSpentTime, 0),
    totalOfRemainingTime: chunks.reduce(
      (sum, c) => sum + c.totalOfRemainingTime,
      0,
    ),
    days: dedupByKey(
      chunks.flatMap((c) => c.days),
      (d) => d.date,
    ),
    weeks: dedupByKey(
      chunks.flatMap((c) => c.weeks),
      (w) => w.week.from,
    ),
    months: dedupByKey(
      chunks.flatMap((c) => c.months),
      (m) => m.month.from,
    ),
  };
}

function createSelector(getState: () => State): Selector {
  return {
    isWorkLoadsChunkPending(view, resourceUuid, chunkKey) {
      return (
        findEntry(getState(), view, resourceUuid)?.chunks[chunkKey]?.request
          .isPending ?? false
      );
    },
    isWorkLoadsChunkSuccess(view, resourceUuid, chunkKey) {
      return (
        findEntry(getState(), view, resourceUuid)?.chunks[chunkKey]?.request
          .isSuccessful ?? false
      );
    },
    isWorkLoadsChunkFailure(view, resourceUuid, chunkKey) {
      return (
        findEntry(getState(), view, resourceUuid)?.chunks[chunkKey]?.request
          .isFailure ?? false
      );
    },
    isWorkLoadsChunkLoaded(view, resourceUuid, chunkKey) {
      const slot = findEntry(getState(), view, resourceUuid)?.chunks[chunkKey];
      return slot ? slot.request.isSuccessful : false;
    },
    isWorkLoadsChunkDirty(view, resourceUuid, chunkKey) {
      return (
        findEntry(getState(), view, resourceUuid)?.chunks[chunkKey]?.isDirty ??
        false
      );
    },
    isAnyWorkLoadsChunkPending(view, resourceUuids, chunkKeys) {
      const state = getState();
      return resourceUuids.some((uuid) => {
        const entry = findEntry(state, view, uuid);
        if (!entry) return false;
        return chunkKeys.some((k) => entry.chunks[k]?.request.isPending);
      });
    },
    getMergedWorkLoads(view, resourceUuids, chunkKeys): WorkLoadsCalendar {
      const state = getState();
      const merged: WorkLoadsCalendar = [];
      for (const uuid of resourceUuids) {
        const entry = findEntry(state, view, uuid);
        if (!entry) continue;
        const loaded = chunkKeys
          .map((k) => entry.chunks[k]?.workLoad)
          .filter((wl): wl is WorkLoad => !!wl);
        if (loaded.length === 0) continue;
        merged.push(mergeChunks(loaded));
      }
      return merged;
    },
  };
}

export { createSelector };
